import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { KemCardStyleItem } from '@/constants/kemCardStylesList'
import { KemCardDTO, CardInfoFromWallet } from '@/domain/wallet/walletTypes'
import { useGetKemCardEncrypted, useGetKemCard } from '@/domain/wallet/useCases'
import { useGenerateSessionId } from '@/infra/rain'
import { useDeviceShare } from '@/hooks/useDeviceShare'
import { useUserStore } from '@/store/useUserStore'
import { purpleCard } from '@/assets/images/kemCardStyles'
import { responsiveAdapterVertical } from '@/utils'

interface KemCardProps {
  kemCardStyleItem?: KemCardStyleItem
  defaultCardNumber?: string
  onPress?: (style: KemCardStyleItem) => void
  /** @deprecated Use cardInfo instead. This is for backward compatibility only */
  infos?: KemCardDTO
  /** Non-sensitive card info from wallet */
  cardInfo?: CardInfoFromWallet
  /** Card ID for fetching encrypted data */
  kemCardId?: string
  kemCardEnabled?: boolean
  skeletonActive?: boolean
  isToShowAllCardInfos?: boolean
  containerStyle?: React.CSSProperties
  onToggleDetails?: (isVisible: boolean) => void
  kemCardImage?: string
}

export const KemCard: React.FC<KemCardProps> = ({
  kemCardStyleItem,
  defaultCardNumber,
  onPress,
  infos,
  cardInfo,
  kemCardId,
  kemCardEnabled = true,
  skeletonActive,
  isToShowAllCardInfos,
  containerStyle,
  onToggleDetails,
  kemCardImage,
}) => {
  const { t } = useTranslation()
  const { copy } = useDeviceShare()
  const [isDisplayingInfos, setIsDisplayingInfos] = useState(
    isToShowAllCardInfos === true,
  )

  const authenticatedUser = useUserStore((state) => state.userAuthenticated)

  // Get the card ID from props or cardInfo
  const cardId = kemCardId || cardInfo?.id

  // Determine card provider - default to 'interlace' for backward compatibility
  const cardProvider = cardInfo?.card_provider || 'interlace'
  const isRainProvider = cardProvider === 'rain'

  // Generate session for RAIN encryption (only for RAIN provider)
  const { data: session, isLoading: isSessionLoading } = useGenerateSessionId()

  // Track toggle count for refetching Interlace card data
  const [toggleCount, setToggleCount] = useState(0)

  // Fetch encrypted card data for RAIN provider
  const {
    data: encryptedCardData,
    isLoading: isLoadingEncryptedData,
    isError: isEncryptedDataError,
  } = useGetKemCardEncrypted(
    {
      card_id: cardId || '',
      session_id: session?.sessionId || '',
      secret_key: session?.secretKey || '',
    },
    {
      enabled: isRainProvider && isDisplayingInfos && !!cardId && !!session,
    },
  )

  // Fetch plaintext card data for Interlace provider
  const {
    data: interlaceCardData,
    isLoading: isLoadingInterlaceData,
    isError: isInterlaceDataError,
  } = useGetKemCard({
    cardId: cardId || '',
    enabled: !isRainProvider && isDisplayingInfos && !!cardId,
    toggleCount,
  })

  const hasInfos = !!infos || !!cardInfo
  const isLoading =
    skeletonActive ||
    (isRainProvider
      ? isLoadingEncryptedData || isSessionLoading
      : isLoadingInterlaceData)

  // Check if there was a fetch/decryption error
  const hasFetchError =
    isDisplayingInfos &&
    (isRainProvider ? isEncryptedDataError : isInterlaceDataError)

  // Get full name
  const fullName = authenticatedUser
    ? `${authenticatedUser.first_name} ${authenticatedUser.last_name}`
    : ''

  // Determine if we can display sensitive info
  const canDisplayInfos = kemCardEnabled && isDisplayingInfos && !isLoading

  const cardStyle = kemCardStyleItem || {
    id: 'default',
    key: 'singularity' as const,
    translationKey: 'cardStyleNames.singularity',
    gradient: ['#1a1a1a', '#2d2d2d'],
    textColor: '#ffffff',
  }

  const handleCardPress = () => {
    if (kemCardStyleItem && onPress) {
      onPress(kemCardStyleItem)
    }
  }

  const toggleCardDetails = () => {
    const newState = !isDisplayingInfos
    setIsDisplayingInfos(newState)
    // Increment toggle count to force refetch for Interlace provider
    if (newState && !isRainProvider) {
      setToggleCount((prev) => prev + 1)
    }
    onToggleDetails?.(newState)
  }

  // Get card number from appropriate source based on provider
  const getActiveCardNumber = (): string | undefined => {
    if (isRainProvider) {
      return encryptedCardData?.cardNumberDecrypted
    }
    return interlaceCardData?.card_number || infos?.card_number
  }

  // Get CVV from appropriate source based on provider
  const getActiveCvv = (): string | undefined => {
    if (isRainProvider) {
      return encryptedCardData?.cvvDecrypted
    }
    return interlaceCardData?.cvv || infos?.cvv
  }

  // Copy handlers using data from appropriate source
  const handleCopyCardNumber = () => {
    const cardNumber = getActiveCardNumber()
    if (cardNumber) {
      copy(
        cardNumber,
        t('screens:kemCard.cardNumberCopied', 'Card number copied'),
      )
    }
  }

  const handleCopyCVV = () => {
    const cvv = getActiveCvv()
    if (cvv) {
      copy(String(cvv), t('screens:kemCard.cvvCopied', 'CVV copied'))
    }
  }

  // Format card number for display
  const formatCardNumber = (cardNumber?: string) => {
    if (!cardNumber) return 'XXXX XXXX XXXX XXXX'
    return (
      cardNumber
        .replace(/\s/g, '')
        .match(/.{1,4}/g)
        ?.join(' ') || cardNumber
    )
  }

  // Determine if this is a style selection card
  const isStyleSelectionCard = !hasInfos && onPress && kemCardStyleItem

  // Get display data based on provider type
  const getCardNumber = (): string => {
    if (!canDisplayInfos) return ''

    if (isRainProvider) {
      return encryptedCardData?.cardNumberDecrypted || ''
    }
    // Interlace provider - check interlaceCardData first, then legacy infos
    return interlaceCardData?.card_number || infos?.card_number || ''
  }

  const getCvv = (): string => {
    if (!canDisplayInfos) return ''

    if (isRainProvider) {
      return encryptedCardData?.cvvDecrypted || ''
    }
    // Interlace provider
    return interlaceCardData?.cvv || infos?.cvv || ''
  }

  const getExpiration = (): { month: string; year: string } | null => {
    if (!canDisplayInfos) return null

    if (isRainProvider && encryptedCardData) {
      return {
        month: String(encryptedCardData.exp_month).padStart(2, '0'),
        year: String(encryptedCardData.exp_year),
      }
    }
    // Interlace provider
    const data = interlaceCardData || infos
    if (data) {
      return {
        month: String(data.exp_month).padStart(2, '0'),
        year: String(data.exp_year),
      }
    }
    return null
  }

  const lastFourDigits =
    cardInfo?.last_four ||
    interlaceCardData?.card_number?.slice(-4) ||
    infos?.card_number?.slice(-4) ||
    '****'

  const cardNumber = getCardNumber()
  const cvv = getCvv()
  const expiration = getExpiration()

  // Format expiration for display
  const formattedExpiration = expiration
    ? `${expiration.month}/${expiration.year.slice(-2)}`
    : '••/••'

  return (
    <BaseView
      data-component="kem-card"
      style={{
        position: 'relative',
        width: '100%',
        cursor: onPress ? 'pointer' : 'default',
        opacity: !kemCardEnabled ? 0.6 : 1,
        ...containerStyle,
      }}
      onClick={handleCardPress}
      className="ym-hide-content"
    >
      {/* Card Background */}
      <BaseView
        style={{
          width: '100%',
          height: responsiveAdapterVertical(220),
          borderRadius: 12,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Card Image Background */}
        <img
          src={kemCardImage || purpleCard}
          alt="Kem Card"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 12,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />

        {/* Simplified overlay for style selection cards */}
        {isStyleSelectionCard ? (
          <BaseView
            style={{
              position: 'absolute',
              left: '20px',
              bottom: '20px',
            }}
          >
            <BaseText
              fontSize={16}
              fontFamily="medium"
              style={{ color: cardStyle.textColor }}
            >
              {t(kemCardStyleItem.translationKey)}
            </BaseText>
          </BaseView>
        ) : (
          /* Full card content for regular cards */
          <BaseView
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {/* Top Section - Eye Icon */}
            <BaseView
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
              }}
            >
              {hasInfos && kemCardEnabled && (
                <BaseView
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    paddingTop: '0px',
                    paddingRight: '0px',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isLoading ? 0.5 : 1,
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!isLoading) {
                      toggleCardDetails()
                    }
                  }}
                >
                  {isLoading && isDisplayingInfos ? (
                    <BaseView
                      style={{
                        width: '24px',
                        height: '24px',
                        border: '2px solid transparent',
                        borderTop: `2px solid ${cardStyle.textColor}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                  ) : (
                    <AppIcons
                      name={canDisplayInfos ? 'eye' : 'eyeOff'}
                      size={24}
                      unthemedColor={cardStyle.textColor}
                    />
                  )}
                </BaseView>
              )}
            </BaseView>

            {/* Card Number */}
            <BaseView
              data-component="kem-card-number"
              style={{
                flex: 1,
                justifyContent: 'center',
                paddingBottom: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor:
                  hasInfos && canDisplayInfos && cardNumber
                    ? 'pointer'
                    : 'default',
              }}
              onClick={
                hasInfos && canDisplayInfos && cardNumber && !isLoading
                  ? handleCopyCardNumber
                  : undefined
              }
            >
              {hasInfos ? (
                <BaseView
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  {(cardNumber
                    ? formatCardNumber(cardNumber)
                    : `XXXX XXXX XXXX ${lastFourDigits}`
                  )
                    .split(' ')
                    .map((group, index) => (
                      <BaseText
                        key={index}
                        fontSize={24}
                        fontFamily="semibold"
                        style={{
                          color: cardStyle.textColor,
                          fontFamily: 'monospace',
                        }}
                      >
                        {group}
                      </BaseText>
                    ))}
                </BaseView>
              ) : (
                <BaseText
                  fontSize={20}
                  style={{
                    color: cardStyle.textColor,
                    whiteSpace: 'nowrap',
                    letterSpacing: '2px',
                    textAlign: 'center',
                    width: '100%',
                    fontFamily: 'monospace',
                  }}
                >
                  {defaultCardNumber || 'XXXX XXXX XXXX XXXX'}
                </BaseText>
              )}
            </BaseView>

            {/* Bottom Section */}
            <BaseView
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* User Name */}
              {hasInfos && fullName && (
                <BaseText
                  fontSize={14}
                  fontFamily="regular"
                  style={{ color: cardStyle.textColor }}
                >
                  {fullName}
                </BaseText>
              )}

              {/* CVV and EXP Row */}
              <BaseView
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '17px',
                }}
              >
                {hasInfos ? (
                  <>
                    {/* CVV Block */}
                    <BaseView
                      style={{
                        cursor: canDisplayInfos && cvv ? 'pointer' : 'default',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      onClick={
                        canDisplayInfos && cvv ? handleCopyCVV : undefined
                      }
                    >
                      <BaseText
                        fontSize={14}
                        fontFamily="regular"
                        style={{ color: cardStyle.textColor, opacity: 0.7 }}
                      >
                        CVV
                      </BaseText>
                      <BaseView
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <BaseText
                          fontSize={14}
                          style={{
                            color: cardStyle.textColor,
                            fontFamily: 'monospace',
                            minWidth: '30px',
                            textAlign: 'left',
                          }}
                        >
                          {cvv || '•••'}
                        </BaseText>
                      </BaseView>
                    </BaseView>

                    {/* EXP Block */}
                    <BaseView
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <BaseText
                        fontSize={14}
                        fontFamily="regular"
                        style={{ color: cardStyle.textColor, opacity: 0.7 }}
                      >
                        EXP
                      </BaseText>
                      <BaseText
                        fontSize={14}
                        style={{
                          color: cardStyle.textColor,
                          fontFamily: 'monospace',
                          minWidth: '40px',
                          textAlign: 'left',
                        }}
                      >
                        {formattedExpiration}
                      </BaseText>
                    </BaseView>
                  </>
                ) : !isStyleSelectionCard ? (
                  <BaseText
                    fontSize={12}
                    fontFamily="regular"
                    style={{ color: cardStyle.textColor, opacity: 0.8 }}
                  >
                    VISA
                  </BaseText>
                ) : null}
              </BaseView>
            </BaseView>
          </BaseView>
        )}

        {/* Decryption Error Overlay */}
        {hasFetchError && kemCardEnabled && (
          <BaseView
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
            }}
          >
            <AppIcons name="warning" size={32} unthemedColor="#ffffff" />
            <BaseText
              fontSize={14}
              fontFamily="semibold"
              style={{ color: '#ffffff', textAlign: 'center' }}
            >
              {t(
                'screens:kemCard.unableToLoadDetails',
                'Unable to load card details',
              )}
            </BaseText>
            <BaseView
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: '8px 16px',
                borderRadius: '20px',
                cursor: 'pointer',
                marginTop: '4px',
              }}
              onClick={(e) => {
                e.stopPropagation()
                setIsDisplayingInfos(false)
                // Increment toggle count to force refetch
                if (!isRainProvider) {
                  setToggleCount((prev) => prev + 1)
                }
                // Reset and allow retry
                setTimeout(() => setIsDisplayingInfos(true), 100)
              }}
            >
              <BaseText
                fontSize={12}
                fontFamily="medium"
                style={{ color: '#ffffff' }}
              >
                {t('common:retry', 'Retry')}
              </BaseText>
            </BaseView>
          </BaseView>
        )}

        {/* Disabled Overlay */}
        {!kemCardEnabled && (
          <BaseView
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BaseView
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                padding: '8px 16px',
                borderRadius: '20px',
              }}
            >
              <BaseText fontSize={12} fontFamily="semibold" color="white">
                Card Locked
              </BaseText>
            </BaseView>
          </BaseView>
        )}
      </BaseView>
    </BaseView>
  )
}

export { createDisplayCardInfos } from './utils'
